import type { ReactNode } from 'react';
import { formatiereZahl } from './WertEinheit';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * GRUPPENZEILEN, SUMMENFUSS, KOPFGRUPPEN (TX3, TX-STANDARD S1.3)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Die rechnende Hälfte der Gruppen in `DataTable` - ohne React, deshalb
 * einzeln prüfbar. Der Excel-Kern (`Tuv.Berichte.Layout.Excel.Excelsummen`)
 * rechnet nach DERSELBEN Regel; beide Seiten prüfen sie an derselben
 * Beispieldatei `tabellengruppen.beispiel.json` (TX-STANDARD S0.5: eine Zahl,
 * eine Darstellung).
 *
 * FEHLSTELLENREGEL (S0.4: fehlende Daten werden gezeigt, nicht erfunden)
 * ---------------------------------------------------------------------
 * - Ein Wert zählt nur, wenn er eine endliche Zahl ist. `null`, `undefined`,
 *   leerer Text, `NaN`, ±∞ und alles, was keine Zahl ist, sind FEHLSTELLEN.
 * - Eine Fehlstelle ist NICHT null: Sie geht in keine Summe, kein Minimum und
 *   keinen Mittelwert ein. Sie wird gezählt und neben dem Ergebnis genannt
 *   („2 ohne Wert").
 * - Hat keine Zeile einen Wert, ist das Ergebnis selbst eine Fehlstelle
 *   („—"), nicht 0.
 * - `schranke`: Für Größen, die nur wachsen können (Druckverlust, Länge,
 *   Leistung), ist eine unvollständige Summe eine UNTERE SCHRANKE und steht
 *   als „≥ 1.234" da - der wahre Wert ist größer, um wie viel, weiß niemand.
 * - Summiert wird in der Reihenfolge der Zeilen, ungerundet. Gerundet wird
 *   nur die Anzeige (S1.7).
 */

/** Wie die Werte einer Spalte im Fuß und in der Gruppenzeile zusammengefasst werden. */
export type Zusammenfassung = 'summe' | 'min' | 'max' | 'mittel' | 'anzahl';

/**
 * Fuß einer Spalte.
 * - eine `Zusammenfassung` oder `{ art, schranke }`: aus den Zeilen gerechnet;
 * - `{ fest, fehlend, schranke }`: der Wert des Laufs vom Server (z. B. eine
 *   Summe, die der Rechenkern anders bildet als „Zeilen addieren"). Er gilt
 *   für die GANZE Liste; ist die Liste gefiltert, steht er nicht da, und die
 *   Tabelle sagt, warum. In Gruppenzeilen steht er nie.
 */
export type FussDefinition =
  | Zusammenfassung
  | { art: Zusammenfassung; schranke?: boolean }
  | { fest: number | null; fehlend?: number; schranke?: boolean };

/** Ergebnis der Zusammenfassung einer Spalte über eine Zeilenmenge. */
export interface Fusswert {
  /** Das Ergebnis; `null` = es gibt keinen Wert (Fehlstelle). */
  wert: number | null;
  /** Zeilen mit Wert. */
  gezaehlt: number;
  /** Zeilen ohne Wert - nie still mitgerechnet. */
  fehlend: number;
  /** Untere Schranke, wenn unvollständig (aus der Definition). */
  schranke: boolean;
  /** Wert des Laufs (fest), nicht aus den Zeilen. */
  fest: boolean;
}

/** Ist das eine Zahl, die in eine Zusammenfassung eingeht? */
export function istWert(w: unknown): w is number {
  return typeof w === 'number' && Number.isFinite(w);
}

/** Die Art einer Fußdefinition (für feste Werte `null`). */
export function fussArt(d: FussDefinition): Zusammenfassung | null {
  if (typeof d === 'string') return d;
  return 'art' in d ? d.art : null;
}

/**
 * Fasst Werte zusammen - die Fehlstellenregel oben. `anzahl` zählt die
 * Zeilen, nicht die Werte (eine Zeile ohne Wert ist trotzdem ein Eintrag).
 */
export function fasseZusammen(
  werte: readonly unknown[],
  art: Zusammenfassung,
  schranke = false,
): Fusswert {
  if (art === 'anzahl') {
    return { wert: werte.length, gezaehlt: werte.length, fehlend: 0, schranke: false, fest: false };
  }

  let gezaehlt = 0;
  let fehlend = 0;
  let summe = 0;
  let min = Infinity;
  let max = -Infinity;

  for (const w of werte) {
    if (!istWert(w)) { fehlend += 1; continue; }
    gezaehlt += 1;
    summe += w;
    if (w < min) min = w;
    if (w > max) max = w;
  }

  let wert: number | null = null;
  if (gezaehlt > 0) {
    if (art === 'summe') wert = summe;
    else if (art === 'min') wert = min;
    else if (art === 'max') wert = max;
    else wert = summe / gezaehlt;
  }

  // Eine untere Schranke ergibt nur die Summe; Minimum, Maximum und Mittel
  // einer unvollständigen Menge sind nach keiner Seite gebunden.
  return { wert, gezaehlt, fehlend, schranke: schranke && art === 'summe', fest: false };
}

/** Fußwert einer Spalte über die gegebenen Zeilen (oder der feste Wert). */
export function fussWert(definition: FussDefinition, werte: readonly unknown[]): Fusswert {
  if (typeof definition === 'object' && 'fest' in definition) {
    return {
      wert: istWert(definition.fest) ? definition.fest : null,
      gezaehlt: 0,
      fehlend: definition.fehlend ?? 0,
      schranke: definition.schranke ?? false,
      fest: true,
    };
  }
  const art = typeof definition === 'string' ? definition : definition.art;
  const schranke = typeof definition === 'string' ? false : (definition.schranke ?? false);
  return fasseZusammen(werte, art, schranke);
}

/** Der angezeigte Wert: deutsch, gerundet nur hier, „≥" bei unvollständiger Schranke, „—" ohne Wert. */
export function fussText(f: Fusswert, nachkomma?: number): string {
  if (f.wert === null) return '—';
  const zahl = formatiereZahl(f.wert, nachkomma);
  return f.schranke && f.fehlend > 0 ? `≥ ${zahl}` : zahl;
}

/** Der Fehlstellenhinweis neben dem Wert, oder `null`, wenn vollständig. */
export function fehlstellenText(f: Fusswert): string | null {
  if (f.fehlend <= 0) return null;
  return `${formatiereZahl(f.fehlend)} ohne Wert`;
}

/**
 * Der Vermerk zu unvollständigen Spalten einer Gruppen- oder Summenzeile -
 * derselbe Wortlaut in CSV und Excel (`Excelsummen.Vermerk`):
 * „unvollständig: Druckverlust 2 ohne Wert (untere Schranke), Länge 1 ohne Wert".
 */
export function fehlstellenVermerk(
  spalten: readonly { titel: string; wert: Fusswert }[],
): string | null {
  const teile = spalten
    .filter((s) => s.wert.fehlend > 0)
    .map((s) => `${s.titel} ${formatiereZahl(s.wert.fehlend)} ohne Wert${
      s.wert.schranke ? ' (untere Schranke)' : ''}`);
  return teile.length === 0 ? null : `unvollständig: ${teile.join(', ')}`;
}

/* ═══════════════════════════════════════════════════════════════════════════
   GRUPPEN
   ═══════════════════════════════════════════════════════════════════════════ */

export interface Gruppierung<Z> {
  /** Gruppenschlüssel je Zeile. */
  nach: (zeile: Z) => string;
  /** Klartext der Gruppe (auch in CSV und Excel). Vorgabe: der Schlüssel. */
  titel?: (schluessel: string, zeilen: Z[]) => string;
  /** Zusätzliches in der Gruppenzeile (z. B. `StatusBadge` „3 prüfen"). Nur Seite. */
  zusatz?: (schluessel: string, zeilen: Z[]) => ReactNode;
  /**
   * Farbstreifen links (Reihenfarbe 1-8, `--diagramm-n`), z. B. je
   * Kostengruppe. Nie allein: Der Titel trägt die Bedeutung.
   */
  markierung?: (schluessel: string) => number | undefined;
  /**
   * Reihenfolge der Gruppen. `eingang` (Vorgabe): wie die Zeilen kamen - der
   * Server ordnet fachlich, eine zweite Ordnung hier widerspräche ihm.
   * Die Sortierung der Tabelle ordnet nur INNERHALB der Gruppen.
   */
  ordnung?: 'eingang' | 'alphabet' | ((a: string, b: string) => number);
  /** Beim Öffnen zugeklappt: Schlüssel oder `'alle'`. */
  zugeklappt?: string[] | 'alle';
  /** Auf- und Zuklappen anbieten. Vorgabe `true`. */
  aufklappbar?: boolean;
}

export interface Gruppe<Z> {
  schluessel: string;
  titel: string;
  /** Die (gefilterten, sortierten) Zeilen der Gruppe. */
  zeilen: Z[];
  /** Zeilen der Gruppe ohne Suche - für „3 von 12". */
  gesamt: number;
}

/** Die Gruppenreihenfolge aus der Eingangsreihenfolge (oder nach `ordnung`). */
export function gruppenOrdnung<Z>(zeilen: readonly Z[], g: Gruppierung<Z>): string[] {
  const folge: string[] = [];
  const gesehen = new Set<string>();
  for (const z of zeilen) {
    const s = g.nach(z);
    if (!gesehen.has(s)) { gesehen.add(s); folge.push(s); }
  }
  const o = g.ordnung ?? 'eingang';
  if (o === 'alphabet') {
    return [...folge].sort((a, b) => a.localeCompare(b, 'de', { numeric: true, sensitivity: 'base' }));
  }
  if (typeof o === 'function') return [...folge].sort(o);
  return folge;
}

/**
 * Teilt die (gefilterten, sortierten) Zeilen in Gruppen. Die Reihenfolge der
 * Gruppen kommt aus ALLEN Zeilen (ungefiltert, unsortiert), damit eine Gruppe
 * beim Sortieren oder Suchen nicht springt. Leere Gruppen fallen weg.
 */
export function gruppiereZeilen<Z>(
  alle: readonly Z[],
  gezeigt: readonly Z[],
  g: Gruppierung<Z>,
): Gruppe<Z>[] {
  const jeGruppe = new Map<string, Z[]>();
  for (const z of gezeigt) {
    const s = g.nach(z);
    const liste = jeGruppe.get(s);
    if (liste) liste.push(z); else jeGruppe.set(s, [z]);
  }
  const gesamt = new Map<string, number>();
  for (const z of alle) {
    const s = g.nach(z);
    gesamt.set(s, (gesamt.get(s) ?? 0) + 1);
  }

  return gruppenOrdnung(alle, g)
    .filter((s) => jeGruppe.has(s))
    .map((s) => {
      const zeilen = jeGruppe.get(s)!;
      return {
        schluessel: s,
        titel: g.titel ? g.titel(s, zeilen) : s,
        zeilen,
        gesamt: gesamt.get(s) ?? zeilen.length,
      };
    });
}

/** Text der Gruppenzeile: „Titel (12)" bzw. gefiltert „Titel (3 von 12)". */
export function gruppenBeschriftung(gr: { titel: string; zeilen: unknown[]; gesamt: number }): string {
  const n = gr.zeilen.length;
  return n === gr.gesamt
    ? `${gr.titel} (${formatiereZahl(n)})`
    : `${gr.titel} (${formatiereZahl(n)} von ${formatiereZahl(gr.gesamt)})`;
}

/** Eine Zeile der Darstellung: Gruppenzeile oder Datenzeile. */
export type Anzeigezeile<Z> =
  | { art: 'gruppe'; gruppe: Gruppe<Z>; fortsetzung: boolean; offen: boolean }
  | { art: 'zeile'; zeile: Z; gruppe?: string };

/**
 * Die flache Folge, die die Tabelle darstellt (und virtualisiert): je Gruppe
 * die Gruppenzeile, dann - wenn offen - ihre Zeilen.
 *
 * `von`/`bis` grenzen die DATENzeilen ein (Blättern). Beginnt eine Seite
 * mitten in einer Gruppe, steht oben ihre Gruppenzeile als Fortsetzung - eine
 * Zeile ohne ihre Gruppe wäre auf der Seite nicht zuzuordnen.
 */
export function anzeigezeilen<Z>(
  gruppen: Gruppe<Z>[],
  istOffen: (schluessel: string) => boolean,
  von = 0,
  bis = Infinity,
): Anzeigezeile<Z>[] {
  const aus: Anzeigezeile<Z>[] = [];
  let index = 0;
  for (const gr of gruppen) {
    const anfang = index;
    const ende = index + gr.zeilen.length;
    index = ende;
    if (ende <= von || anfang >= bis) continue;
    const offen = istOffen(gr.schluessel);
    aus.push({ art: 'gruppe', gruppe: gr, fortsetzung: anfang < von, offen });
    if (!offen) continue;
    const erste = Math.max(von, anfang) - anfang;
    const letzte = Math.min(bis, ende) - anfang;
    for (let i = erste; i < letzte; i += 1) {
      aus.push({ art: 'zeile', zeile: gr.zeilen[i], gruppe: gr.schluessel });
    }
  }
  return aus;
}

/* ═══════════════════════════════════════════════════════════════════════════
   KOPFGRUPPEN (mehrzeiliger Kopf, z. B. VDI 3814)
   ═══════════════════════════════════════════════════════════════════════════ */

/** Was die Kopfberechnung von einer Spalte braucht. */
export interface KopfSpalte {
  schluessel: string;
  /** Pfad der Kopfgruppen über dem Titel, von oben nach unten. */
  gruppe?: string | string[];
  /** Zusatzzeile unter dem Titel (z. B. Spaltennummer). */
  kopfZusatz?: string;
}

export type KopfZelle<S> =
  | { art: 'gruppe'; titel: string; colSpan: number; rowSpan: number; erste: string }
  | { art: 'spalte'; spalte: S; colSpan: 1; rowSpan: number };

export function gruppenPfad(s: KopfSpalte): string[] {
  if (s.gruppe === undefined) return [];
  return (Array.isArray(s.gruppe) ? s.gruppe : [s.gruppe]).filter((t) => t !== '');
}

/**
 * Die Kopfzeilen über den sichtbaren Spalten.
 *
 * - Ebene `e` fasst benachbarte Spalten zusammen, deren Gruppenpfad bis
 *   einschließlich `e` GLEICH ist - nicht nur dieselbe Beschriftung auf
 *   dieser Ebene: Zwei Untergruppen „Meldung" unter verschiedenen Gruppen
 *   bleiben zwei Zellen.
 * - Eine Spalte mit kürzerem Pfad steht mit `rowSpan` bis zur Titelzeile
 *   durch.
 * - Gibt es eine Zusatzzeile, endet der Titel über ihr; sie hat je Spalte
 *   eine eigene Zelle.
 *
 * Ohne Gruppen und Zusatz kommt genau EINE Zeile heraus - die Tabelle bleibt
 * dann so, wie sie vor TX3 war.
 */
export function kopfzeilen<S extends KopfSpalte>(spalten: readonly S[]): {
  zeilen: KopfZelle<S>[][];
  zusatz: boolean;
  tiefe: number;
} {
  const pfade = spalten.map(gruppenPfad);
  const tiefe = pfade.reduce((m, p) => Math.max(m, p.length), 0);
  const zusatz = spalten.some((s) => s.kopfZusatz !== undefined && s.kopfZusatz !== '');
  const zeilen: KopfZelle<S>[][] = [];

  const gleich = (a: string[], b: string[], bis: number) => {
    for (let i = 0; i <= bis; i += 1) if (a[i] !== b[i]) return false;
    return true;
  };

  for (let e = 0; e <= tiefe; e += 1) {
    const zeile: KopfZelle<S>[] = [];
    let i = 0;
    while (i < spalten.length) {
      const p = pfade[i];
      if (p.length === e) {
        zeile.push({ art: 'spalte', spalte: spalten[i], colSpan: 1, rowSpan: tiefe - e + 1 });
        i += 1;
      } else if (p.length > e) {
        let j = i + 1;
        while (j < spalten.length && pfade[j].length > e && gleich(pfade[j], p, e)) j += 1;
        zeile.push({
          art: 'gruppe', titel: p[e], colSpan: j - i, rowSpan: 1, erste: spalten[i].schluessel,
        });
        i = j;
      } else {
        i += 1;
      }
    }
    zeilen.push(zeile);
  }

  return { zeilen, zusatz, tiefe };
}
